import { useTranslate } from '@refinedev/core';
import { Tag } from 'antd';
import type { ExecutionSource } from '@/api/generated/schemas';

// ─── ExecutionSourceTag ───────────────────────────────────────────────────────

const EXECUTION_SOURCE_COLOR: Record<string, string> = {
  api: 'blue',
  admin_api: 'purple',
  publisher_api: 'cyan',
  worker: 'gold',
  system: 'default',
};

export const ExecutionSourceTag = ({
  source,
}: {
  source: ExecutionSource | string;
}) => {
  const t = useTranslate();
  return (
    <Tag color={EXECUTION_SOURCE_COLOR[source] ?? 'default'}>
      {t(`system.activities.source.${source}`, source)}
    </Tag>
  );
};

// ─── ActivityVerbTag ──────────────────────────────────────────────────────────

/** Colour by the verb's domain namespace — the part before the first dot. */
const ACTIVITY_VERB_DOMAIN_COLOR: Record<string, string> = {
  user: 'blue',
  profile: 'cyan',
  oauth_account: 'magenta',
  review: 'purple',
  royalty_report: 'gold',
  settlement: 'orange',
  master_file: 'geekblue',
  sample: 'lime',
};

/**
 * Outcomes that went wrong read as red whatever their domain — in a scan of an
 * audit stream the failed attempt is the row worth spotting.
 */
const ACTIVITY_VERB_FAILURE_SUFFIXES = [
  '_failed',
  '_rejected',
  '.rejected',
  '.deleted',
  '.blocked',
];

const verbColor = (verb: string): string => {
  if (ACTIVITY_VERB_FAILURE_SUFFIXES.some((s) => verb.endsWith(s))) {
    return 'red';
  }
  const domain = verb.split('.')[0] ?? '';
  return ACTIVITY_VERB_DOMAIN_COLOR[domain] ?? 'default';
};

/**
 * The verb, verbatim. These are dotted identifiers rather than prose — a
 * translation of each would drift the moment the backend adds one, and the
 * people who read an audit stream read the identifier.
 */
export const ActivityVerbTag = ({ verb }: { verb: string }) => (
  <Tag color={verbColor(verb)} style={{ fontFamily: 'monospace' }}>
    {verb}
  </Tag>
);
